<template>
  <div class="schedule-hours">
    <div class="hours-head">
      <div>
        <h5 class="hours-title">Horários de atendimento</h5>
        <p class="hours-hint">
          Cada linha é um horário. Clique para editar ou duplique para usar como base de
          um novo; marque vários para alterar convênios, serviços e profissionais ou
          excluir de uma vez.
        </p>
      </div>
      <span class="hours-summary" v-if="hours && hours.length > 0">
        {{ hoursSummary }}
      </span>
    </div>
    <div class="loading loading-lg" v-if="loadingHours" />
    <template v-else>
      <div class="empty hours-empty-state" v-if="hours && hours.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'clock']" size="2x"/>
        </div>
        <p class="empty-title h5">Horários de atendimento</p>
        <p class="empty-subtitle">Nenhum horário configurado para esta agenda.</p>
        <button class="btn btn-primary btn-icon btn-icon-left mt-2" @click="$emit('add')">
          <fa-icon :icon="['fal', 'plus']"/>Adicionar horários de atendimento
        </button>
      </div>

      <template v-else>
        <div class="hours-group is-week">
          <div class="hours-group-head">
            <span class="hours-group-title">
              <fa-icon :icon="['fal', 'calendar-alt']"/>Semanais
              <span class="hours-group-count">{{ weekHours.length }}</span>
            </span>
            <a
              href="#"
              class="hours-group-select"
              v-if="weekHours.length > 1"
              @click.prevent="toggleAll(weekHours)"
            >{{ allSelected(weekHours) ? 'Desmarcar todos' : 'Selecionar todos' }}</a>
          </div>
          <div class="hour-list">
            <div
              class="hour-row"
              :class="{ 'is-selected': item.selected }"
              v-for="item in weekHours"
              :key="item.id"
              @click="$emit('edit', item)"
            >
              <span class="hour-row-select" @click.stop="toggleHour(item)">
                <label class="form-checkbox hour-row-check" @click.prevent>
                  <input type="checkbox" :checked="item.selected" tabindex="-1">
                  <i class="form-icon"></i>
                </label>
              </span>
              <span class="hour-row-day">{{ weekLabel(item) }}</span>
              <span class="hour-row-ranges">
                <template v-if="item.ranges && item.ranges.length > 0">
                  <span class="range-pill" v-for="(r, i) in item.ranges" :key="i">
                    {{ r.start }}–{{ r.end }}
                  </span>
                </template>
                <span class="range-pill is-slot" v-else>Exclusivo encaixe</span>
                <span class="range-mode" v-if="item.arrivalOrder">ordem de chegada</span>
              </span>
              <span class="hour-row-counts" v-if="hasCounts(item)">
                <span :class="{ 'is-empty': !item.professionals }">
                  <fa-icon :icon="['fal', 'user-md']"/>{{ item.professionals }}
                </span>
                <span :class="{ 'is-empty': !item.insurances }">
                  <fa-icon :icon="['fal', 'shield-check']"/>{{ item.insurances }}
                </span>
                <span :class="{ 'is-empty': !item.expenses }">
                  <fa-icon :icon="['fal', 'stethoscope']"/>{{ item.expenses }}
                </span>
              </span>
              <span class="hour-row-go">
                <button
                  class="btn btn-sm btn-action btn-icon btn-gray hour-copy tooltip tooltip-left"
                  data-tooltip="Duplicar"
                  @click.stop="$emit('duplicate', item)"
                ><fa-icon :icon="['fal', 'copy']"/></button>
                <fa-icon :icon="['fal', 'chevron-right']"/>
              </span>
            </div>
            <div class="hour-row-add">
              <button
                class="btn btn-primary btn-sm btn-icon btn-icon-left"
                @click="$emit('add')"
              ><fa-icon :icon="['fal', 'plus']"/>Adicionar horário</button>
              <span class="hour-row-add-hint">
                Novo dia da semana ou uma escala em datas específicas
              </span>
            </div>
          </div>
        </div>

        <div class="hours-group is-scale" v-if="showScaleGroup">
          <div class="hours-group-head">
            <span class="hours-group-title">
              <fa-icon :icon="['fal', 'calendar-day']"/>Escalas
            </span>
            <div class="hours-group-tools">
              <a
                href="#"
                class="hours-group-select"
                v-if="visibleScale.length > 1"
                @click.prevent="toggleAll(visibleScale)"
              >{{ allSelected(visibleScale) ? 'Desmarcar todos' : 'Selecionar todos' }}</a>
              <div class="btn-group">
                <button
                  class="btn btn-sm view-toggle"
                  :class="{ 'is-on': scaleView === 'future' }"
                  @click="scaleView = 'future'"
                >Próximas ({{ futureScale.length }})</button>
                <button
                  class="btn btn-sm view-toggle"
                  :class="{ 'is-on': scaleView === 'past' }"
                  @click="scaleView = 'past'"
                >Passadas ({{ pastScale.length }})</button>
              </div>
              <div class="period-filter" :class="{ 'has-error': $v.period.$error }">
                <st-date-period
                  :start-date.sync="period.from"
                  :end-date.sync="period.to"
                  :max-months="6"
                  :periods="scalePeriods"
                  :disabled="loadingScale"
                  @blur:startDate="$v.period.from.$touch()"
                  @blur:endDate="$v.period.to.$touch()"
                />
                <button
                  class="btn btn-sm btn-secondary btn-icon btn-icon-left"
                  :class="{ loading: loadingScale }"
                  :disabled="loadingScale"
                  @click="applyPeriod"
                ><fa-icon :icon="['fal', 'filter']"/>Filtrar</button>
                <a href="#" class="period-clear" v-if="periodApplied"
                   @click.prevent="clearPeriod">limpar</a>
              </div>
            </div>
          </div>

          <div class="loading loading-lg" v-if="loadingScale"/>

          <div class="hours-empty" v-else-if="visibleScale.length === 0">
            {{ scaleView === 'future'
              ? 'Nenhuma escala futura no período.'
              : 'Nenhuma escala passada no período.' }}
          </div>

          <div
            class="hours-month"
            v-for="group in scaleGroups"
            :key="group.key"
            v-show="!loadingScale"
          >
            <div class="hours-month-head">
              <span class="hours-month-title">{{ group.label }}</span>
              <a
                href="#"
                class="hours-group-select"
                @click.prevent="toggleAll(group.items)"
              >
                {{ allSelected(group.items) ? 'Desmarcar' : 'Selecionar' }}
                todos de {{ group.month }}
              </a>
            </div>
            <div class="hour-list">
              <div
                class="hour-row"
                :class="{ 'is-selected': item.selected, 'is-past': isPast(item) }"
                v-for="item in group.items"
                :key="item.id"
                @click="$emit('edit', item)"
              >
                <span class="hour-row-select" @click.stop="toggleHour(item)">
                  <label class="form-checkbox hour-row-check" @click.prevent>
                    <input type="checkbox" :checked="item.selected" tabindex="-1">
                    <i class="form-icon"></i>
                  </label>
                </span>
                <span class="hour-row-day">
                  <strong>{{ item.day | date('DD') }}</strong>
                  <small>{{ item.day | date('ddd') }}</small>
                </span>
                <span class="hour-row-ranges">
                  <template v-if="item.ranges && item.ranges.length > 0">
                    <span class="range-pill" v-for="(r, i) in item.ranges" :key="i">
                      {{ r.start }}–{{ r.end }}
                    </span>
                  </template>
                  <span class="range-pill is-slot" v-else>Exclusivo encaixe</span>
                  <span class="range-mode" v-if="item.arrivalOrder">ordem de chegada</span>
                </span>
                <span class="hour-row-counts" v-if="hasCounts(item)">
                  <span :class="{ 'is-empty': !item.professionals }">
                    <fa-icon :icon="['fal', 'user-md']"/>{{ item.professionals }}
                  </span>
                  <span :class="{ 'is-empty': !item.insurances }">
                    <fa-icon :icon="['fal', 'shield-check']"/>{{ item.insurances }}
                  </span>
                  <span :class="{ 'is-empty': !item.expenses }">
                    <fa-icon :icon="['fal', 'stethoscope']"/>{{ item.expenses }}
                  </span>
                </span>
                <span class="hour-row-go">
                  <button
                    class="btn btn-sm btn-action btn-icon btn-gray hour-copy tooltip tooltip-left"
                    data-tooltip="Duplicar"
                    @click.stop="$emit('duplicate', item)"
                  ><fa-icon :icon="['fal', 'copy']"/></button>
                  <fa-icon :icon="['fal', 'chevron-right']"/>
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>

    <div class="selection-bar" v-if="selectedHoursCount > 0">
      <span class="selection-text">
        <strong>{{ selectedHoursCount }}</strong>
        {{ selectedHoursCount === 1 ? 'horário selecionado' : 'horários selecionados' }}
      </span>
      <span class="selection-actions">
        <button class="btn btn-sm btn-link" @click="setSelection(selectedHours, false)">
          Limpar seleção <kbd>Esc</kbd>
        </button>
        <button
          class="btn btn-sm btn-primary btn-icon btn-icon-left"
          :disabled="deletingHour"
          @click="bulkItems.show = true"
        ><fa-icon :icon="['fal', 'layer-group']"/>Convênios, serviços e profissionais</button>
        <button
          class="btn btn-sm btn-error btn-icon btn-icon-left"
          :class="{ loading: deletingHour }"
          :disabled="deletingHour"
          @click="removeSelectedHours"
        ><fa-icon :icon="['fal', 'trash']"/>Excluir</button>
      </span>
    </div>

    <bulk-items
      v-if="bulkItems.show"
      :show="bulkItems.show"
      :ids="selectedHours.map(({ id }) => id)"
      @saved="reload"
      @close="bulkItems.show = false"
    />
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import moment from 'moment';
import { date, minDate, maxDate } from '@/data/validators';
import { resetCache } from '@/helpers/api';
import BulkItems from './modals/BulkItems.vue';

const WEEK_ORDER = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

function weekIndex(day) {
  const name = String(day || '').toLowerCase();
  return WEEK_ORDER.findIndex(prefix => name.startsWith(prefix));
}

export default {
  components: {
    BulkItems,
  },
  props: {
    scheduleId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      hours: [],
      loadingHours: false,
      loadingScale: false,
      deletingHour: false,
      bulkItems: { show: false },
      scaleView: 'future',
      period: { from: '', to: '' },
      periodApplied: false,
    };
  },
  mounted() {
    this.loadHours();
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  watch: {
    scheduleId() {
      this.loadHours();
    },
  },
  computed: {
    selectedHoursCount() {
      return this.hours.filter(h => h.selected).length;
    },
    selectedHours() {
      return this.hours.filter(h => h.selected);
    },
    weekHours() {
      return this.hours
        .filter(item => item.type === 'week')
        .slice()
        .sort((a, b) => {
          const order = i => (weekIndex(i.day) === 0 ? 7 : weekIndex(i.day));
          return order(a) - order(b);
        });
    },
    scaleHours() {
      return this.hours
        .filter(item => item.type === 'scale')
        .slice()
        .sort((a, b) => (a.day > b.day ? 1 : -1));
    },
    futureScale() {
      return this.scaleHours.filter(item => !this.isPast(item));
    },
    pastScale() {
      return this.scaleHours.filter(item => this.isPast(item)).reverse();
    },
    visibleScale() {
      return this.scaleView === 'future' ? this.futureScale : this.pastScale;
    },
    scaleGroups() {
      const groups = [];
      this.visibleScale.forEach((item) => {
        const key = moment(item.day).format('YYYY-MM');
        let group = groups.find(g => g.key === key);
        if (!group) {
          const label = moment(item.day).format('MMMM [de] YYYY');
          group = {
            key,
            label: label.charAt(0).toUpperCase() + label.slice(1),
            month: moment(item.day).format('MMMM'),
            items: [],
          };
          groups.push(group);
        }
        group.items.push(item);
      });
      return groups;
    },
    showScaleGroup() {
      return this.scaleHours.length > 0 || this.periodApplied || this.loadingScale;
    },
    scalePeriods() {
      return [
        { value: 'month', text: 'Este mês' },
        { value: 'd:30', text: 'Próximos 30 dias' },
        { value: 'd:90', text: 'Próximos 3 meses' },
        { value: 'd:180', text: 'Próximos 6 meses' },
      ];
    },
    hoursSummary() {
      const ranges = this.hours.reduce((total, item) => total + (item.ranges || []).length, 0);
      return `${this.hours.length} ${this.hours.length === 1 ? 'horário' : 'horários'} · `
        + `${ranges} ${ranges === 1 ? 'faixa' : 'faixas'}`;
    },
  },
  validations() {
    const rules = {
      period: {
        from: { required, date },
        to: { required, date },
      },
    };

    if (this.period.from && moment(this.period.from, 'YYYY-MM-DD', true).isValid()) {
      rules.period.to = {
        required,
        date,
        minDate: minDate(this.period.from),
        maxDate: maxDate(moment(this.period.from).add(6, 'months')),
      };
    }

    return rules;
  },
  methods: {
    reload() {
      return this.loadHours();
    },
    onKeydown(event) {
      if (event.key !== 'Escape' || this.selectedHoursCount === 0) return;
      if (document.querySelector('.modal.active')) return;
      this.setSelection(this.selectedHours, false);
    },
    applyPeriod() {
      this.$v.period.$touch();
      if (this.$v.period.$error) return;
      this.periodApplied = true;
      this.loadHours({ scaleOnly: true });
    },
    clearPeriod() {
      this.period = { from: '', to: '' };
      this.periodApplied = false;
      this.$v.period.$reset();
      this.loadHours({ scaleOnly: true });
    },
    async loadHours({ scaleOnly = false } = {}) {
      if (!this.scheduleId) {
        this.hours = [];
        return;
      }

      if (scaleOnly) {
        this.loadingScale = true;
      } else {
        this.loadingHours = true;
        this.$emit('loading', true);
      }

      const params = {};
      if (this.periodApplied && this.period.from && this.period.to) {
        params.startDate = this.period.from;
        params.endDate = this.period.to;
      }

      await this.$httpX
        .get(`/schedules/${this.scheduleId}`, { params })
        .then(({ data }) => {
          const selected = new Set(this.hours.filter(h => h.selected).map(h => h.id));
          this.hours = (data.schedules || []).map(item => ({
            ...item,
            selected: selected.has(item.id),
          }));
        })
        .catch(() => {
          this.$toast.show('Erro ao carregar os horários', { type: 'error' });
        })
        .finally(() => {
          this.loadingHours = false;
          this.loadingScale = false;
          this.$emit('loading', false);
        });
    },
    weekLabel(item) {
      return item.day.charAt(0).toUpperCase() + item.day.slice(1);
    },
    isPast(item) {
      return item.day < moment().format('YYYY-MM-DD');
    },
    toggleHour(item) {
      this.$set(item, 'selected', !item.selected);
    },
    allSelected(items) {
      return items.length > 0 && items.every(h => h.selected);
    },
    toggleAll(items) {
      this.setSelection(items, !this.allSelected(items));
    },
    setSelection(items, value) {
      items.forEach((item) => {
        this.$set(item, 'selected', value);
      });
    },
    hasCounts(item) {
      return ['professionals', 'insurances', 'expenses']
        .some(key => typeof item[key] === 'number');
    },
    removeSelectedHours() {
      const count = this.selectedHoursCount;
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Deseja realmente excluir ${count} horário${count === 1 ? '' : 's'}?</div>`
          + '</div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.deletingHour = true;
              close();
              const ids = this.hours.filter(h => h.selected).map(h => h.id);
              this.$httpX
                .delete('/schedule-config', { data: { ids } })
                .then(() => {
                  this.hours = this.hours.filter(h => !h.selected);
                  resetCache();
                  this.$emit('removed', ids);
                })
                .catch(() => {
                  this.$toast.show('Erro ao excluir os horários', { type: 'error' });
                })
                .finally(() => {
                  this.deletingHour = false;
                });
            },
          },
        ],
      });
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';

.schedule-hours {
  .hours-head {
    align-items: flex-end;
    border-bottom: $border-width solid $border-color;
    display: flex;
    gap: $layout-spacing;
    justify-content: space-between;
    margin: $layout-spacing-lg 0 $layout-spacing-lg;
    padding-bottom: $layout-spacing;
  }
  .hours-title {
    color: $secondary-color-dark;
    font-size: $font-size-lg;
    font-weight: 600;
    margin: 0;
  }
  .hours-hint {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin: $layout-spacing-sm 0 0;
  }
  .hours-summary {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    white-space: nowrap;
  }

  .hours-empty-state {
    padding: $layout-spacing-lg;
    .empty-icon {
      color: lighten($primary-color, 20%);
      margin-bottom: $layout-spacing-sm;
    }
    .empty-title {
      font-size: $font-size-lg;
      margin-bottom: $layout-spacing-sm;
    }
    .empty-subtitle {
      font-size: $font-size-sm;
      margin-bottom: $layout-spacing;
    }
    .btn {
      margin-top: 0 !important;
    }
  }
  .hours-group {
    border: $border-width solid;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg $layout-spacing-lg;
    &.is-week {
      background-color: $gray-color-ultra-light;
      border-color: $border-color;
      .hours-group-title svg, .hours-group-count {
        color: $primary-color;
      }
      .hours-group-count {
        border-color: lighten($primary-color, 26%);
      }
    }
    &.is-scale {
      background-color: #FFF8E6;
      border-color: #F2C94C;
      .hours-group-title svg, .hours-group-count {
        color: #8A5A00;
      }
      .hours-group-count {
        border-color: #F2C94C;
      }
      .hour-row.is-selected {
        background-color: #FFF3D1;
      }
    }
  }
  .hours-group-head {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing;
    justify-content: space-between;
    margin-bottom: $layout-spacing;
  }
  .hours-group-select {
    font-size: $font-size-xs;
    font-weight: 600;
    &:focus {
      box-shadow: none;
      outline: 0;
    }
  }
  .selection-bar {
    align-items: center;
    background-color: $dark-color;
    border-radius: $border-radius * 2;
    bottom: $layout-spacing;
    box-shadow: 0 8px 24px rgba($dark-color, .35);
    color: $light-color;
    display: flex;
    gap: $layout-spacing-lg;
    justify-content: space-between;
    margin: $layout-spacing auto 0;
    max-width: 40rem;
    padding: $layout-spacing $layout-spacing-lg;
    position: sticky;
    z-index: 2;
    .selection-text {
      color: $light-color;
      font-size: $font-size-sm;
      strong {
        background-color: $primary-color;
        border-radius: 1rem;
        color: $light-color;
        display: inline-block;
        line-height: 1;
        margin-right: .2rem;
        min-width: 1rem;
        padding: .2rem .4rem;
        text-align: center;
      }
    }
    .btn-link {
      color: $gray-color-light;
      &:hover {
        color: $light-color;
      }
      kbd {
        background-color: rgba($light-color, .12);
        border: $border-width solid rgba($light-color, .25);
        border-radius: $border-radius;
        box-shadow: none;
        color: $gray-color-light;
        font-size: .5rem;
        line-height: 1;
        margin-left: .2rem;
        padding: .1rem .25rem;
        vertical-align: middle;
      }
    }
    .selection-actions {
      align-items: center;
      display: flex;
      gap: $layout-spacing-sm;
    }
  }
  .hours-group-title {
    align-items: center;
    color: $gray-color-900;
    display: inline-flex;
    font-size: $font-size-xs;
    font-weight: 700;
    gap: $layout-spacing-sm;
    letter-spacing: .04em;
    text-transform: uppercase;
  }
  .hours-group-count {
    background-color: $light-color;
    border: 1px solid;
    border-radius: 1rem;
    font-size: .5rem;
    line-height: 1;
    padding: .14rem .3rem;
  }
  .hours-group-tools {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing;
    .view-toggle {
      background-color: $light-color;
      border-color: $border-color-dark;
      color: $gray-color-dark;
      &:hover {
        color: $secondary-color-dark;
      }
      &.is-on {
        background-color: $gray-color-ultra-light;
        border-color: $border-color-dark;
        color: $secondary-color-dark;
        font-weight: 600;
      }
      &:focus {
        box-shadow: none;
      }
    }
    .btn,
    .btn-group .btn {
      font-size: $font-size-sm;
      height: 1.4rem;
      line-height: 1.2rem;
      padding: .05rem .4rem;
    }
    .btn-icon {
      padding: .05rem .3rem;
      svg {
        height: .7rem;
        width: .7rem;
      }
    }
  }
  .period-filter {
    align-items: center;
    display: flex;
    gap: $layout-spacing;
    .input-group {
      align-items: center;
    }
    .form-input,
    input,
    .input-group-addon,
    .date-period-dropdown,
    .date-period-dropdown .btn,
    .date-period-dropdown .dropdown-toggle {
      font-size: $font-size-sm !important;
      height: 1.4rem !important;
      line-height: 1.2rem !important;
      min-height: 0 !important;
      padding: .05rem .3rem !important;
    }
    .form-input,
    input {
      text-align: left !important;
      width: 4.8rem !important;
    }
    .input-group-addon {
      align-items: center;
      display: inline-flex;
      justify-content: center;
      width: 1.6rem;
      svg {
        height: .6rem;
        width: .6rem;
      }
    }
    .period-clear {
      font-size: $font-size-xs;
    }
    &.has-error .form-input {
      border-color: $error-color;
    }
  }
  .hours-month {
    margin-bottom: $layout-spacing;
  }
  .hours-month-head {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: $layout-spacing-sm;
  }
  .hours-month-title {
    color: $gray-color-900;
    font-size: $font-size-sm;
    font-weight: 700;
  }
  .hours-empty {
    border: $border-width dashed $border-color-dark;
    border-radius: $border-radius;
    color: $gray-color-dark;
    font-size: $font-size-sm;
    padding: $layout-spacing-lg;
    text-align: center;
  }

  .hour-list {
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    box-shadow: 0 1px 2px rgba($dark-color, .06);
    overflow: hidden;
  }
  .hour-row {
    align-items: center;
    border-bottom: $border-width solid $border-color;
    cursor: pointer;
    display: grid;
    gap: $layout-spacing-lg;
    grid-template-columns: .9rem 7rem minmax(0, 1fr) auto auto;
    padding: $layout-spacing $layout-spacing-lg;
    transition: background-color .15s ease;
    &:hover {
      background-color: $gray-color-ultra-light;
      .hour-row-check {
        opacity: 1;
      }
      .hour-row-go {
        color: $primary-color;
      }
      .hour-copy {
        opacity: 1;
      }
    }
    &.is-selected {
      background-color: lighten($primary-color, 34%);
      .hour-row-check {
        opacity: 1;
      }
    }
    &.is-past {
      .hour-row-day, .range-pill {
        color: $gray-color-dark;
      }
    }
  }
  .hour-row-select {
    align-self: stretch;
    cursor: pointer;
    display: flex;
    margin: (-$layout-spacing) 0 (-$layout-spacing) (-$layout-spacing-lg);
    padding: 0 $layout-spacing 0 $layout-spacing-lg;
    &:hover .hour-row-check {
      opacity: 1;
      .form-icon {
        border-color: $primary-color;
      }
    }
  }
  .hour-row-check {
    align-self: center;
    margin: 0;
    min-height: 0;
    opacity: .45;
    padding: 0 0 0 .9rem;
    pointer-events: none;
    transition: opacity .15s ease;
    .form-icon {
      height: .8rem;
      left: 0;
      top: -.4rem;
      width: .8rem;
    }
  }
  .hour-row-day {
    color: $secondary-color-dark;
    font-weight: 600;
    white-space: nowrap;
    strong {
      font-size: $font-size-lg;
      font-variant-numeric: tabular-nums;
      margin-right: .2rem;
    }
    small {
      color: $gray-color-dark;
      font-weight: 500;
      text-transform: capitalize;
    }
  }
  .hour-row-ranges {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: .25rem;
    min-width: 0;
  }
  .range-pill {
    background-color: #FFF8E6;
    border: $border-width solid #F5DD8E;
    border-radius: 1rem;
    color: $gray-color-900;
    font-size: $font-size-xs;
    font-variant-numeric: tabular-nums;
    font-weight: 400;
    line-height: 1;
    padding: .22rem .5rem;
    white-space: nowrap;
    &.is-slot {
      border-color: #F2C94C;
      color: #8A5A00;
    }
  }
  .range-mode {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    margin-left: $layout-spacing-sm;
  }
  .hour-row-counts {
    display: flex;
    font-size: $font-size-xs;
    gap: $layout-spacing-sm;
    white-space: nowrap;
    span {
      align-items: center;
      background-color: $gray-color-ultra-light;
      border-radius: 1rem;
      color: $gray-color-900;
      display: inline-flex;
      font-variant-numeric: tabular-nums;
      font-weight: 600;
      gap: .25rem;
      padding: .15rem .45rem;
      svg {
        color: $secondary-color;
      }
      &.is-empty {
        background-color: #FFF3D1;
        color: #8A5A00;
        svg {
          color: #8A5A00;
        }
      }
    }
  }
  .hour-row-go {
    align-items: center;
    color: $gray-color;
    display: flex;
    gap: $layout-spacing;
    justify-content: flex-end;
  }
  .hour-copy {
    opacity: .6;
    transition: opacity .15s ease;
    &:focus {
      box-shadow: none;
    }
  }
  .hour-row-add {
    align-items: center;
    background-color: $light-color;
    border-top: $border-width dashed $border-color-dark;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg;
    .hour-row-add-hint {
      color: $gray-color-dark;
      font-size: $font-size-xs;
    }
  }
  .hours-month .hour-list {
    margin-bottom: $layout-spacing;
  }

  @media screen and (max-width: $size-sm) {
    .hour-row {
      grid-template-columns: .9rem minmax(0, 1fr) auto;
      .hour-row-ranges {
        grid-column: 2;
      }
      .hour-row-counts {
        grid-column: 2;
      }
    }
  }
}
</style>
